import { Controller } from "@hotwired/stimulus";

// Pull-to-refresh for the installed (standalone) PWA, attached to the app's
// inner scroll pane. iOS Safari has native pull-to-refresh in the browser,
// but home-screen web apps get none, so approving from the phone left no way
// to refresh the queue. Pull down from the top of the pane past the threshold
// and the page reloads via Turbo.
//
// Deliberately inert outside standalone display mode (native PTR covers the
// in-browser case, and doubling up would fight it).
export default class extends Controller {
  static values = {
    threshold: { type: Number, default: 70 },
    max: { type: Number, default: 96 },
  };

  connect() {
    this.pulling = false;
    this.startY = 0;
    this.distance = 0;
    this.refreshing = false;

    this.onTouchStart = this.touchStart.bind(this);
    this.onTouchMove = this.touchMove.bind(this);
    this.onTouchEnd = this.touchEnd.bind(this);

    this.element.addEventListener("touchstart", this.onTouchStart, { passive: true });
    // touchmove must be non-passive: while a pull is armed we preventDefault
    // so the rubber-band doesn't fight the gesture. We bail out on the first
    // line when not armed, so normal scrolling stays cheap.
    this.element.addEventListener("touchmove", this.onTouchMove, { passive: false });
    this.element.addEventListener("touchend", this.onTouchEnd, { passive: true });
    this.element.addEventListener("touchcancel", this.onTouchEnd, { passive: true });
  }

  disconnect() {
    this.element.removeEventListener("touchstart", this.onTouchStart);
    this.element.removeEventListener("touchmove", this.onTouchMove);
    this.element.removeEventListener("touchend", this.onTouchEnd);
    this.element.removeEventListener("touchcancel", this.onTouchEnd);
    this.indicator?.remove();
    this.indicator = null;
  }

  // Checked per-gesture (not at connect) so the mode flip the pwa controller
  // writes on html[data-display-mode] is always respected, and tests can
  // force it.
  standalone() {
    return (
      document.documentElement.dataset.displayMode === "standalone" ||
      window.navigator.standalone === true
    );
  }

  touchStart(event) {
    if (this.refreshing || !this.standalone()) return;
    if (event.touches.length !== 1) return;
    if (this.element.scrollTop > 0) return;

    this.pulling = true;
    this.startY = event.touches[0].clientY;
    this.distance = 0;
  }

  touchMove(event) {
    if (!this.pulling || this.refreshing) return;

    const dy = event.touches[0].clientY - this.startY;
    if (dy <= 0) {
      // Finger moved up: this is a scroll, not a pull.
      this.resetIndicator();
      this.pulling = false;
      return;
    }

    // Ignore tiny jitters so taps and slow scroll starts aren't hijacked.
    if (dy < 8) return;

    event.preventDefault();
    this.distance = Math.min(dy * 0.45, this.maxValue);
    this.renderIndicator();
  }

  touchEnd() {
    if (!this.pulling || this.refreshing) return;
    this.pulling = false;

    if (this.distance >= this.thresholdValue) {
      this.refresh();
    } else {
      this.resetIndicator();
    }
  }

  refresh() {
    this.refreshing = true;
    this.spinIndicator();
    // Replace (not advance) so pull-to-refresh never grows the history stack.
    if (window.Turbo && window.Turbo.visit) {
      window.Turbo.visit(window.location.href, { action: "replace" });
    } else {
      window.location.reload();
    }
  }

  // --- indicator ----------------------------------------------------------

  ensureIndicator() {
    if (this.indicator) return this.indicator;

    const el = document.createElement("div");
    el.setAttribute("data-testid", "pull-to-refresh-indicator");
    el.className =
      "pointer-events-none absolute left-1/2 z-30 flex h-9 w-9 -translate-x-1/2 items-center justify-center " +
      "rounded-full border border-line bg-ink-800 text-fg-muted shadow-lg transition-opacity duration-100";
    el.style.top = "0px";
    el.style.opacity = "0";
    el.innerHTML =
      '<svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">' +
      '<path stroke-linecap="round" d="M4 4v6h6M20 20v-6h-6M20 9A8 8 0 0 0 5.6 5.6L4 10m0 4l1.6 4.4A8 8 0 0 0 20 14"/></svg>';

    const host = this.element.parentElement || this.element;
    if (getComputedStyle(host).position === "static") host.style.position = "relative";
    host.appendChild(el);
    this.indicator = el;
    return el;
  }

  renderIndicator() {
    const el = this.ensureIndicator();
    const progress = Math.min(this.distance / this.thresholdValue, 1);
    el.style.opacity = String(progress);
    el.style.transform = `translateX(-50%) translateY(${this.distance}px) rotate(${progress * 180}deg)`;
  }

  spinIndicator() {
    const el = this.ensureIndicator();
    el.style.opacity = "1";
    el.style.transform = `translateX(-50%) translateY(${this.thresholdValue}px)`;
    el.classList.add("animate-spin");
  }

  resetIndicator() {
    if (!this.indicator) return;
    this.indicator.style.opacity = "0";
    this.indicator.style.transform = "translateX(-50%) translateY(0px)";
    this.indicator.classList.remove("animate-spin");
    this.distance = 0;
  }
}
